import { t } from 'i18next';

import { INGREDIENT_ACTIONS } from '~constants/buildPizza';
import { isComplement } from '~models/product';
import { addFields } from '~utils/object';
import { capitalizeFirstLetter } from '~utils/string';

import { OPPOSITE_SIDE, SIDES } from './constants';

export const getIngredientMultiplier = ingredient =>
  ingredient?.quantity > 1 ? ` x${ingredient?.quantity}` : '';

export const getRemovedIngredients = product =>
  product?.ingredients?.filter(ingredient => ingredient?.action === INGREDIENT_ACTIONS.REMOVE);

export const ingredientsToString = (ingredients, mapFn = ingredient => ingredient?.name) =>
  capitalizeFirstLetter(ingredients?.map(mapFn).join(', '));

export const getIngredientWithMultiplier = ingredient =>
  ingredient?.name + getIngredientMultiplier(ingredient);

export const getIngredientsWithMultiplier = ingredients =>
  ingredientsToString(ingredients, getIngredientWithMultiplier);

export const getIngredientWithAction =
  (baseIngredients = []) =>
  ingredient => {
    const name = ingredient?.name?.toLowerCase();
    return baseIngredients?.some(baseIngredient => baseIngredient?.id === ingredient?.id)
      ? `${name}${getIngredientMultiplier(ingredient)}`
      : `${t(`ProductDetail:${ingredient?.action}`, { ingredient: name })}${getIngredientMultiplier(
          ingredient
        )}`;
  };

export const getIngredientsWithAction = (ingredients, baseIngredients) =>
  ingredientsToString(ingredients, getIngredientWithAction(baseIngredients));

export const findIngredient = (ingredient, ingredients) =>
  ingredients?.find(e => e.id === ingredient.id && e.quantity === ingredient.quantity);

const getPizzaSpecialtyData = (menuableId, pizzasSpecialtyData) =>
  Object.values(pizzasSpecialtyData)?.find(pizzaSpecialty =>
    pizzaSpecialty?.variants?.some(variant => variant?.id === menuableId)
  );

const getCurrentIngredients = ingredients =>
  ingredients?.filter(ingredient => ingredient?.action === INGREDIENT_ACTIONS.ADD);

const mergeIngredientsWithSpecialty = (side, pizzasSpecialtyData) => {
  const pizzaSpecialtyData = getPizzaSpecialtyData(side?.menuableId, pizzasSpecialtyData);
  return getCurrentIngredients(
    pizzaSpecialtyData?.ingredients.reduce(
      (acc, ingredient) =>
        acc.find(element => element?.id === ingredient?.id)
          ? acc
          : [
              ...acc,
              {
                id: ingredient?.id,
                name: ingredient?.name,
                quantity: 1,
                action: INGREDIENT_ACTIONS.ADD
              }
            ],
      side?.ingredients || []
    )
  );
};

export const getIngredientsPerSide = (pizzaByHalves, pizzasSpecialtyData) =>
  [SIDES.LEFT, SIDES.RIGHT].reduce(
    (acc, side) => ({
      ...acc,
      [side]: mergeIngredientsWithSpecialty(pizzaByHalves?.halves?.[side], pizzasSpecialtyData) || []
    }),
    {}
  );

export const getPizzaByHalvesIngredients = (product, pizzasSpecialtyData) => {
  const ingredientsPerSide = getIngredientsPerSide(product, pizzasSpecialtyData);
  return [
    ...addFields(ingredientsPerSide.left, { side: SIDES.LEFT }),
    ...addFields(ingredientsPerSide.right, { side: SIDES.RIGHT })
  ].reduce(
    (acc, ingredient) => {
      if (findIngredient(ingredient, acc[SIDES.WHOLE])) {
        return acc;
      }
      const ingredientFounded = findIngredient(
        ingredient,
        ingredientsPerSide[OPPOSITE_SIDE[ingredient.side]]
      );
      ingredientFounded && ingredientFounded?.quantity === ingredient?.quantity
        ? acc[SIDES.WHOLE].push(ingredient)
        : acc[ingredient.side].push(ingredient);
      return acc;
    },
    { [SIDES.WHOLE]: [], [SIDES.LEFT]: [], [SIDES.RIGHT]: [] }
  );
};

const getOfferProductName = product =>
  [product?.name, !isComplement(product) && product?.variantName]
    .filter(Boolean)
    .join(isComplement(product) ? ', ' : ' ');

export const getOfferDetail = (item, baseIngredients) =>
  item?.groupProducts?.map((product, index) => ({
    id: `${product?.name}-${index}`,
    name: getOfferProductName(product),
    description: isComplement(product)
      ? product?.variantName
      : getIngredientsWithAction(product?.ingredients, baseIngredients)
  }));
